import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { copywritingOrchestrationSelector } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import { useCopywritingSocket } from "@/pages/Private/pages/Messages/components/GenerateBar/useCopywritingSocket";
import { useGetSingleListPersonQuery } from "@/pages/Private/redux/list/list.api";

import { ListPerson } from "../../schema/list";
import { Tabs } from "../../pages/Unassigned/unassignedTabs";
import { listDetailTabForStatus } from "../../utils/buildListDetailLink";
import { CHAT_LIST_PERSON_PARAM } from "../../utils/listDetailChatParam";

interface UseReachOutColumnWiringParams {
	rows: ListPerson[];
	/** A row click is ignored while the panel is already open — it belongs to the panel then. */
	showDetailView: boolean;
	setSelectedListPerson: Dispatch<SetStateAction<ListPerson | undefined>>;
	setShowDetailView: Dispatch<SetStateAction<boolean>>;
	/** The table's own search box — seeded so the jumped-to lead is the visible row. */
	setSearchValue?: (value: string) => void;
}

/**
 * List-table wiring introduced by the Outreach column, extracted from {@link UnassignedTable} to keep
 * that (already-oversized) component shrinking. It bundles three concerns:
 *
 * 1. **Live copywriting socket** — kept alive at page level so AI-draft generation fired from the
 *    reach-out menu (or the detail-panel chat) reflects live on the row's status chip
 *    (QUEUED → RUNNING → COMPLETED) via `updateCopywritingInInbox`. Bound to the generating lead's
 *    prospect, derived from the orchestration slice's `listPersonId`.
 * 2. **Detail-panel open handlers** — every way the panel opens: a plain row click, the Research
 *    tab, and `openChatFor`, which opens it straight on the Chat view for the Outreach column's
 *    "Write manually" / "Review" / "Reply" actions.
 * 3. **The `chatListPersonId` jump** — the page header's AI-drafts queue pill names a lead that may
 *    be on any tab and any page, so it writes the id into the URL and this hook goes and gets it
 *    (see {@link useChatListPersonJump}).
 */
export const useReachOutColumnWiring = ({
	rows,
	showDetailView,
	setSelectedListPerson,
	setShowDetailView,
	setSearchValue,
}: UseReachOutColumnWiringParams) => {
	const { user } = useAppSelector(authSelector);
	const {
		isGenerating,
		alreadyInProgress,
		listPersonId: generatingListPersonId,
	} = useAppSelector(copywritingOrchestrationSelector);

	const generatingProspectId = useMemo(
		() =>
			generatingListPersonId
				? rows.find((row) => row.id === generatingListPersonId)?.prospect?.id
				: undefined,
		[generatingListPersonId, rows]
	);

	useCopywritingSocket({
		companyId: user?.company?.id,
		prospectId: generatingProspectId,
		enabled: isGenerating || alreadyInProgress,
	});

	const [searchParams, setSearchParams] = useSearchParams();
	// listPersonId whose detail panel should open directly on the Chat view (Outreach column
	// "Write manually" / "Review" / "Reply"); null for a normal panel open.
	const [openChatFor, setOpenChatFor] = useState<number | null>(null);

	const handleOpenDetailForRow = useCallback(
		(row: ListPerson) => {
			setSelectedListPerson(row);
			setOpenChatFor(null);
			setShowDetailView(true);
		},
		[setSelectedListPerson, setShowDetailView]
	);

	const handleOpenChatForRow = useCallback(
		(row: ListPerson) => {
			setSelectedListPerson(row);
			setOpenChatFor(row.id);
			setShowDetailView(true);
		},
		[setSelectedListPerson, setShowDetailView]
	);

	/** A plain click anywhere on the row opens the lead panel on its default view. */
	const handleRowClick = useCallback(
		(
			id: number,
			event?: React.MouseEvent<HTMLTableRowElement | HTMLTableCellElement | HTMLInputElement>
		) => {
			// A click that lands on the row's checkbox is a selection, not a panel open.
			if ((event?.target as HTMLElement | undefined)?.localName === "input") {
				return;
			}

			if (showDetailView) {
				return;
			}

			setSelectedListPerson(rows.find((row) => row.id === id));
			setOpenChatFor(null);
			setShowDetailView(true);
		},
		[rows, showDetailView, setSelectedListPerson, setShowDetailView]
	);

	const handleOpenResearchTab = useCallback(
		(row: ListPerson) => {
			const newParams = new URLSearchParams(searchParams);

			newParams.set("detailTab", "RESEARCH");
			setSearchParams(newParams, { replace: true });
			setSelectedListPerson(row);
			setOpenChatFor(null);
			setShowDetailView(true);
		},
		[searchParams, setSearchParams, setSelectedListPerson, setShowDetailView]
	);

	useChatListPersonJump({
		rows,
		searchParams,
		setSearchParams,
		setSearchValue,
		setSelectedListPerson,
		setOpenChatFor,
		setShowDetailView,
	});

	return {
		openChatFor,
		handleRowClick,
		handleOpenDetailForRow,
		handleOpenChatForRow,
		handleOpenResearchTab,
	};
};

interface ChatListPersonJumpParams {
	rows: ListPerson[];
	searchParams: URLSearchParams;
	setSearchParams: (params: URLSearchParams, options?: { replace?: boolean }) => void;
	setSearchValue?: (value: string) => void;
	setSelectedListPerson: Dispatch<SetStateAction<ListPerson | undefined>>;
	setOpenChatFor: Dispatch<SetStateAction<number | null>>;
	setShowDetailView: Dispatch<SetStateAction<boolean>>;
}

/**
 * Consume the `chatListPersonId` URL param: go to that lead on this list and open its panel on the
 * Chat view. The page header's AI-drafts queue pill is the writer — it lives outside the table and
 * names a lead that may be on another tab or another page, which is why the hand-off is a URL param
 * rather than a callback (PREC-4103; before this, picking a lead there navigated to the tracker Feed
 * and dropped the lead entirely).
 *
 * The lead is fetched by id when it is not among the loaded rows, so the panel can open on a lead
 * the current page does not contain. Switching tab remounts the table, and the param survives that —
 * so the jump simply resumes against the new tab's instance.
 */
const useChatListPersonJump = ({
	rows,
	searchParams,
	setSearchParams,
	setSearchValue,
	setSelectedListPerson,
	setOpenChatFor,
	setShowDetailView,
}: ChatListPersonJumpParams) => {
	const requestedId = Number(searchParams.get(CHAT_LIST_PERSON_PARAM)) || null;
	const rowLead = requestedId ? rows.find((row) => row.id === requestedId) : undefined;
	const { data: fetchedLead } = useGetSingleListPersonQuery(requestedId ?? 0, {
		skip: !requestedId || !!rowLead,
	});
	const lead = rowLead ?? (fetchedLead?.id === requestedId ? fetchedLead : undefined);

	useEffect(() => {
		if (!requestedId || !lead) {
			return;
		}

		const targetTab = listDetailTabForStatus(lead.assignStatus);
		const currentTab = searchParams.get("tab") ?? Tabs.UNASSIGNED;

		// Tracked profiles are not a leads table, so there is nothing to jump to there — open the
		// panel where the user already is rather than stranding them on a tab with no row.
		if (targetTab !== currentTab && targetTab !== Tabs.TRACKED_PROFILE) {
			const nextParams = new URLSearchParams(searchParams);

			nextParams.set("tab", targetTab);
			setSearchParams(nextParams, { replace: true });

			return;
		}

		if (lead.prospect?.name) {
			setSearchValue?.(lead.prospect.name);
		}

		setSelectedListPerson(lead);
		setOpenChatFor(lead.id);
		setShowDetailView(true);

		const clearedParams = new URLSearchParams(searchParams);

		clearedParams.delete(CHAT_LIST_PERSON_PARAM);
		setSearchParams(clearedParams, { replace: true });
	}, [
		lead,
		requestedId,
		searchParams,
		setOpenChatFor,
		setSearchParams,
		setSearchValue,
		setSelectedListPerson,
		setShowDetailView,
	]);
};
